// Nav panel collapsed/expanded preference (BON-531).
//
// A viewer *choice*, not a viewport fact — which is why it lives in JS while
// the rest of the responsive chrome is CSS.
//
// Three states, not two. `null` means "no choice yet", which CSS resolves per
// breakpoint: icon-only on a tablet, labelled on a desktop. An explicit
// 'collapsed' or 'expanded' then applies at every width, so a tablet user who
// wants to read the item names can have them and a desktop user who wants the
// space back can have that.
//
// Persisted in localStorage so the choice survives a reload and a new tab,
// matching `nebari-view-mode` rather than the sessionStorage-backed
// `nebari-last-section`.

const STORAGE_KEY = 'nebari-nav-collapsed';

/** `null` = follow the breakpoint default. */
export type NavCollapseState = 'collapsed' | 'expanded' | null;

function parse(raw: string | null): NavCollapseState {
  if (raw === 'collapsed' || raw === 'expanded') return raw;
  // Back-compat: the first cut stored a bare boolean string.
  if (raw === 'true') return 'collapsed';
  if (raw === 'false') return 'expanded';
  return null;
}

function read(): NavCollapseState {
  try {
    return parse(localStorage.getItem(STORAGE_KEY));
  } catch {
    // localStorage can be unavailable (incognito, blocked cookies). Falling
    // back to null just means the breakpoint default applies.
    return null;
  }
}

// Cached snapshot. useSyncExternalStore requires getSnapshot to be
// referentially stable between changes.
let snapshot: NavCollapseState = read();
const listeners = new Set<() => void>();

export function subscribeNavCollapsed(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getNavCollapsed(): NavCollapseState {
  return snapshot;
}

export function setNavCollapsed(next: NavCollapseState) {
  if (snapshot === next) return;
  snapshot = next;
  try {
    if (next) localStorage.setItem(STORAGE_KEY, next);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Persistence is best-effort; the in-memory snapshot still drives this
    // session so the toggle works even when storage is blocked.
  }
  listeners.forEach((l) => l());
}

/** Test seam — reset the in-memory snapshot and the persisted value.
 *  Leaves `listeners` alone: clearing them would sever a mounted component's
 *  useSyncExternalStore subscription. */
export function resetNavCollapsedForTests() {
  snapshot = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}
